import {useMutation} from '@apollo/client';
import {
  DeleteTournamentData,
  DeleteTournamentVars,
  UpdateTournamentData,
  UpdateTournamentVars,
  CreateTournamentData,
  CreateTournamentVars,
  StartTournamentData,
  StartTournamentVars,
} from 'packages/common-types/mutation-types';
import {
  DELETE_TOURNAMENT,
  UPDATE_TOURNAMENT,
  CREATE_TOURNAMENT,
  START_TOURNAMENT,
} from 'packages/graphql/v2/mutations';
import {GET_TOURNAMENTS} from 'packages/graphql/v2/queries';
import {useState, useCallback} from 'react';

/**
 * This hook allows to you to CRUD tournaments.
 */
const useTournament = () => {
  const [isLoading, setLoading] = useState(false);

  const [startMutation] = useMutation<StartTournamentData, StartTournamentVars>(
    START_TOURNAMENT
  );

  const [deleteMutation] = useMutation<
    DeleteTournamentData,
    DeleteTournamentVars
  >(DELETE_TOURNAMENT);

  const [editMutation] = useMutation<
    UpdateTournamentData,
    UpdateTournamentVars
  >(UPDATE_TOURNAMENT);

  const [addMutation] = useMutation<CreateTournamentData, CreateTournamentVars>(
    CREATE_TOURNAMENT
  );

  const startTournament = useCallback(
    async (variables: StartTournamentVars) => {
      setLoading(true);
      const res = await startMutation({variables});
      setLoading(false);
      return res;
    },
    [startMutation]
  );

  const deleteTournament = useCallback(
    async (variables: DeleteTournamentVars) => {
      setLoading(true);
      const res = await deleteMutation({
        variables,
        refetchQueries: [
          {
            query: GET_TOURNAMENTS,
          },
        ],
      });
      setLoading(false);
      return res;
    },
    [deleteMutation]
  );

  const editTournament = useCallback(
    async (variables: UpdateTournamentVars) => {
      setLoading(true);
      const res = await editMutation({variables});
      setLoading(false);
      return res;
    },
    [editMutation]
  );

  const addTournament = useCallback(
    async (variables: CreateTournamentVars) => {
      setLoading(true);
      const res = await addMutation({variables});
      setLoading(false);
      return res;
    },
    [addMutation]
  );

  return {
    startTournament,
    deleteTournament,
    editTournament,
    addTournament,
    isLoading,
  };
};

export default useTournament;
